import {
  APIFieldAttributes,
  APIFieldType,
  ArdoqId,
  DateTimeFieldAttribute,
  TimelineField,
  TimelinesGridPrecisionUnit,
} from '@ardoq/api-types';
import { isTruthy, WILDCARD } from '@ardoq/common-helpers';
import type { I18nLocale } from '@ardoq/i18n';
import {
  DateRangeFieldAttributes,
  dateRangeOperations,
  MAX_DATE,
  MIN_DATE,
} from '@ardoq/date-range';
import { addHours, endOfDay, max, min } from '@ardoq/date-time';
import {
  DEFAULT_MARGIN,
  GraphGroup,
  getDimensions,
  GraphNode,
  GraphItem,
  isNode,
} from '@ardoq/graph';
import {
  CollapsibleIds,
  CollapsedBooleanProperties,
  DateRangeWithOptionalDates,
  FieldsRegistryItem,
  TimelineItemModel,
  TimelineViewGraphHierarchy,
  TimelineViewModelData,
  TimelineViewNodeHierarchyInfo,
  RelevantCompontsScopeDataMap,
  ViewRelevantWorkspaceScopeData,
  TimelinePhasesWithRenderingParameters,
  TimelinePhase,
  Milestone,
  ItemPhasesRow,
  ViewRelevantComponentScopeData,
  TimelineLabelWithRenderingParameters,
  WorkspaceAndFieldsRegistry,
  RawGraphItemsSortComparator,
  ViewModelSourceData,
} from '../types';
import { getRawGraphItemsComparator } from './viewModelUtils';
import {
  EXPANDER_WIDTH,
  TIMELINE_CHILD_INDENTATION,
  TIMELINE_LABEL_WITH_ICON_COLUMN_PADDINGS,
  TIMELINE_MILESTONE_SIZE,
  TIMELINE_LABEL_COLUMN_PADDINGS,
  TIMELINE_FIELD_DATA_EXPANDER_WIDTH,
  TIMELINE_LABEL_CONTAINER_PADDING_X,
  MARGIN_BETWEEN_ROWS,
} from '../items/consts';
import {
  MAX_MIN_VISIBLE_LABEL_WIDTH,
  MAX_MIN_VISIBLE_LABEL_WIDTH_MODERNIZED,
} from '../consts';
import { groupBy } from 'lodash';
import { WithPopoverPlacement } from '@ardoq/popovers';
import {
  getPhasesRowsHeightForItem,
  getRenderedItemPhases,
} from '../items/phases/calculations';
import {
  getModernizedItemTreeIndentation,
  getTotalLabelsHeight,
} from '../items/calculations';
import { TIMELINE_CHART_DIMENSIONS_MARGIN_TOP } from './consts';

/**
 * Replaces the domain bounds with the minimum and maximum of the given dates and the domain bounds.
 */
export const replaceDomainBounds = (
  itemDates: Array<Date | null>,
  chartDomain: DateRangeWithOptionalDates
): [Date, Date] => [
  min([...itemDates, chartDomain[0]].filter(isTruthy)),
  max([...itemDates, chartDomain[1]].filter(isTruthy)),
];

type GetFieldsRelevantForFilteringBySelectedFieldsArgs = {
  isFilterBySelectedFields: boolean | undefined;
  selectedFields: TimelineField[] | undefined;
  allDateRangeFields: DateRangeFieldAttributes[];
  allMilestoneFields: APIFieldAttributes[];
};
export const getFieldsRelevantForFilteringBySelectedFields = ({
  isFilterBySelectedFields,
  selectedFields,
  allDateRangeFields,
  allMilestoneFields,
}: GetFieldsRelevantForFilteringBySelectedFieldsArgs) => {
  if (!isFilterBySelectedFields || !selectedFields?.length) {
    return {
      relevantMilestoneFields: allMilestoneFields,
      relevantDateRangeFields: allDateRangeFields,
    };
  }
  const selectedFieldsSet = new Set(
    selectedFields?.map(({ name }) => name) ?? []
  );
  const selectedDateRangeFieldsSet = new Set(
    selectedFields
      ?.filter(({ name }) => {
        if (dateRangeOperations.fieldNameIsDateRangeStartField(name)) {
          const endFieldName = dateRangeOperations.toEndDateName(
            dateRangeOperations.extractDateRangeFieldName(name)
          );
          return selectedFieldsSet.has(endFieldName);
        }
        return false;
      })
      .map(({ name }) => dateRangeOperations.extractDateRangeFieldName(name)) ??
      []
  );
  return {
    relevantMilestoneFields: allMilestoneFields.filter(({ name }) =>
      selectedFieldsSet.has(name)
    ),
    relevantDateRangeFields: allDateRangeFields.filter(
      ({ name }) =>
        selectedFieldsSet.has(name) || selectedDateRangeFieldsSet.has(name)
    ),
  };
};

/**
 * This is needed for backward compatibility with the old date range field Ids.
 */
const dateRangeFieldIdsToAPIFieldId = (
  field: DateRangeFieldAttributes
): string =>
  `${field.dateTimeFields.start._id},${field.dateTimeFields.end._id}`;

/**
 * Field registry item is a simplified version of the field that is used for legend and menus.
 */
export const fieldToRegistryItem = (
  field: DateRangeFieldAttributes | APIFieldAttributes
): FieldsRegistryItem => ({
  name: field.name,
  label: field.label,
  _id: dateRangeOperations.isDateRangeField(field)
    ? dateRangeFieldIdsToAPIFieldId(field)
    : field._id,
});

const isFieldSelected = <
  T extends APIFieldAttributes | DateTimeFieldAttribute | FieldsRegistryItem,
>(
  field: T,
  selectedFieldsViewSetting: string[]
): boolean =>
  selectedFieldsViewSetting.includes(
    dateRangeOperations.isDateRangeField(field)
      ? dateRangeFieldIdsToAPIFieldId(field)
      : field._id
  );

/**
 * Selected fields view setting can contain fields that are not present.
 * To maintain the order of the fields, we need to filter out the fields that are not present in the relevant fields.
 */
export const getSelectedRelevantDateFieldsOrder = <
  T extends APIFieldAttributes | DateTimeFieldAttribute | FieldsRegistryItem,
>(
  relevantFields: T[],
  selectedFieldsViewSetting: string[]
): T[] =>
  selectedFieldsViewSetting.includes(WILDCARD)
    ? relevantFields
    : relevantFields.filter(field =>
        isFieldSelected(field, selectedFieldsViewSetting)
      );

/** Get all descendants of an item including the item itself. */
export const withAllDescendants = (
  nodeId: string,
  filteredViewGraphHierarchy: TimelineViewGraphHierarchy
): string[] => {
  const children = filteredViewGraphHierarchy.get(nodeId)?.children;
  const result = [nodeId];

  return (
    children?.reduce(
      (acc: string[], { id }) =>
        acc.concat(withAllDescendants(id, filteredViewGraphHierarchy)),
      result
    ) ?? result
  );
};

type GetCollapsedBooleanPropertiesArgs = {
  collapsedIds: Set<string>;
  collapsibleIds: CollapsibleIds;
};
/**
 * The 4 returned properties are used to determine the state of the settings bar buttons
 */
export const getCollapsedBooleanProperties = ({
  collapsedIds,
  collapsibleIds,
}: GetCollapsedBooleanPropertiesArgs): CollapsedBooleanProperties => {
  const { rootCollapsible, collapsible } = collapsibleIds;
  const hasNoCollapsible = collapsible.size === 0;

  if (hasNoCollapsible) {
    return {
      areAllCollapsed: false,
      areAllExpanded: false,
      areAllRootItemsCollapsed: false,
      hasNoCollapsible,
    };
  }

  const areAllCollapsed = areAllItemsCollapsed(collapsedIds, collapsible);

  const areAllExpanded = collapsedIds.size === 0;
  const areAllRootItemsCollapsed =
    areAllCollapsed ||
    Boolean(
      rootCollapsible?.every(rawItemIdPath => collapsedIds.has(rawItemIdPath))
    );
  return {
    areAllCollapsed,
    areAllExpanded,
    areAllRootItemsCollapsed,
    hasNoCollapsible,
  };
};

export const mergeCollapsedBooleanProperties = (
  a: CollapsedBooleanProperties,
  b: CollapsedBooleanProperties
): CollapsedBooleanProperties => {
  if (a.hasNoCollapsible) return b;
  if (b.hasNoCollapsible) return a;
  return {
    areAllCollapsed: a.areAllCollapsed && b.areAllCollapsed,
    areAllExpanded: a.areAllExpanded && b.areAllExpanded,
    areAllRootItemsCollapsed:
      a.areAllRootItemsCollapsed && b.areAllRootItemsCollapsed,
    hasNoCollapsible: false,
  };
};

const areAllItemsCollapsed = (
  collapsedIds: Set<string>,
  collapsibleIds: Set<string>
): boolean =>
  collapsedIds.has(WILDCARD) || collapsedIds.size === collapsibleIds.size;

/** Return space taken by elements in labels column, which do not contain the label text (margins, icons, etc.) */
export const getNegativeLabelSpaceForLabelsColumn = (
  item: TimelineItemModel,
  hasSiblingGroup?: boolean
) => {
  const {
    isGroup,
    treeDepth,
    isComponent,
    isFieldDataCollapsible,
    itemLabels: { legacyLabelParts },
  } = item;

  const treeIndentation = legacyLabelParts
    ? TIMELINE_CHILD_INDENTATION * treeDepth
    : getModernizedItemTreeIndentation(treeDepth, isGroup, hasSiblingGroup);

  const groupOffset = isGroup
    ? EXPANDER_WIDTH
    : TIMELINE_LABEL_CONTAINER_PADDING_X;

  return (
    treeIndentation +
    groupOffset +
    (isComponent
      ? TIMELINE_LABEL_WITH_ICON_COLUMN_PADDINGS
      : TIMELINE_LABEL_COLUMN_PADDINGS) +
    (isFieldDataCollapsible ? TIMELINE_FIELD_DATA_EXPANDER_WIDTH : 0)
  );
};

const getLabelsColumnMinWidthReducer = (
  currentMinWidth: number,
  item: TimelineItemModel
): number => {
  const negativeLabelSpace = getNegativeLabelSpaceForLabelsColumn(item);
  const minVisibleLabelWidth = item.itemLabels.legacyLabelParts
    ? MAX_MIN_VISIBLE_LABEL_WIDTH
    : MAX_MIN_VISIBLE_LABEL_WIDTH_MODERNIZED;

  return Math.max(currentMinWidth, negativeLabelSpace + minVisibleLabelWidth);
};

type GetChartDimensionsArgs = Pick<TimelineViewModelData, 'viewModelData'> & {
  width: number;
  height: number;
  userDefinedLabelsColumnWidth: number | null;
};
export const getChartDimensions = ({
  viewModelData,
  width,
  height,
  userDefinedLabelsColumnWidth,
}: GetChartDimensionsArgs) => {
  const deepestLabelMinWidth = viewModelData.reduce(
    getLabelsColumnMinWidthReducer,
    0
  );

  const leftMargin = Math.max(
    deepestLabelMinWidth,
    userDefinedLabelsColumnWidth ? userDefinedLabelsColumnWidth : width / 3
  );

  return {
    labelsColumnMinWidth: deepestLabelMinWidth,
    dimensions: getDimensions(
      { width, height },
      {
        top: TIMELINE_CHART_DIMENSIONS_MARGIN_TOP,
        left: leftMargin,
        bottom: 0,
        right: DEFAULT_MARGIN.right,
      }
    ),
  };
};

type HasAncestorWithNodeId = (args: {
  currentNodeId?: string;
  targetNodeId: string;
  viewGraphHierarchy: TimelineViewGraphHierarchy;
}) => boolean;
const hasAncestorWithNodeId: HasAncestorWithNodeId = ({
  currentNodeId,
  targetNodeId,
  viewGraphHierarchy,
}) => {
  if (!currentNodeId) {
    return false;
  }
  const currentNodeHierarchyInfo = viewGraphHierarchy.get(currentNodeId);
  if (currentNodeHierarchyInfo?.graphItem.id === targetNodeId) {
    return true;
  }
  return hasAncestorWithNodeId({
    currentNodeId: currentNodeHierarchyInfo?.parentGraphNodeId,
    targetNodeId,
    viewGraphHierarchy,
  });
};

type IsRootItemInContextArgs = {
  rootGraphItem: GraphGroup | GraphNode;
  viewGraphHierarchy: TimelineViewGraphHierarchy;
  contextComponentNodeId: string;
};
const isRootItemInContext = ({
  rootGraphItem,
  viewGraphHierarchy,
  contextComponentNodeId,
}: IsRootItemInContextArgs) => {
  // is rootGraphItem the context component node itself?
  if (rootGraphItem.id === contextComponentNodeId) {
    return true;
  }

  // Find the parent of the context node and walk up the tree until we reach the root of the hierarchy
  return hasAncestorWithNodeId({
    currentNodeId: contextComponentNodeId,
    targetNodeId: rootGraphItem.id,
    viewGraphHierarchy,
  });
};

type GetRootItemsForContextComponentArgs = {
  allRootGraphItems: (GraphGroup | GraphNode)[];
  contextComponentId: string;
  viewGraphHierarchy: TimelineViewGraphHierarchy;
};
const getRootItemsForContextComponent = ({
  allRootGraphItems,
  contextComponentId,
  viewGraphHierarchy,
}: GetRootItemsForContextComponentArgs) => {
  // context component can have multiple nodes in the graph
  const contextComponentGraphItemIds = groupBy(
    Array.from(viewGraphHierarchy.values()).map(({ graphItem }) => graphItem),
    'modelId'
  )[contextComponentId]?.map(({ id }) => id);

  if (!contextComponentGraphItemIds?.length) {
    return [];
  }

  return allRootGraphItems.filter(rootGraphItem =>
    contextComponentGraphItemIds.some(contextComponentNodeId =>
      isRootItemInContext({
        rootGraphItem,
        viewGraphHierarchy,
        contextComponentNodeId,
      })
    )
  );
};

export const getViewGraphHierarchyRootItems = (
  viewGraphHierarchy: TimelineViewGraphHierarchy
) =>
  [...viewGraphHierarchy.values()]
    .filter(({ parentGraphNodeId }) => !parentGraphNodeId)
    .map(({ graphItem }) => graphItem);

const getGraphItemComparator =
  (
    viewGraphHierarchy: TimelineViewGraphHierarchy,
    hierarchySortComparator: RawGraphItemsSortComparator
  ) =>
  (a: GraphItem, b: GraphItem): number => {
    const rawGraphItemA = viewGraphHierarchy.get(a.id)?.rawGraphItem;
    const rawGraphItemB = viewGraphHierarchy.get(b.id)?.rawGraphItem;

    if (!rawGraphItemA && !rawGraphItemB) {
      return 0;
    }
    if (!rawGraphItemA) {
      return 1;
    }
    if (!rawGraphItemB) {
      return -1;
    }

    return hierarchySortComparator(rawGraphItemA, rawGraphItemB);
  };

export const sortViewModelSourceDataHierarchy = (
  sourceData: ViewModelSourceData
): ViewModelSourceData => {
  const { fieldsAndItemsSourceData, sortDependencies } = sourceData;

  if (!sortDependencies) {
    return sourceData;
  }

  const { viewGraphHierarchy, startSetNodes } = fieldsAndItemsSourceData;

  const compareGraphItems = getGraphItemComparator(
    viewGraphHierarchy,
    getRawGraphItemsComparator(sortDependencies)
  );

  return {
    ...sourceData,
    fieldsAndItemsSourceData: {
      ...fieldsAndItemsSourceData,
      viewGraphHierarchy: new Map(
        [...viewGraphHierarchy].map(([graphItemId, hierarchyInfo]) => [
          graphItemId,
          {
            ...hierarchyInfo,
            children: hierarchyInfo.children.toSorted(compareGraphItems),
          },
        ])
      ),
      startSetNodes: startSetNodes.toSorted(compareGraphItems),
    },
  };
};

const isComponentOfRelevantType = (
  modelId: string,
  relevantComponentTypeIds: Set<string>,
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap
) => {
  const componentData = viewRelevantComponentsScopeData.get(modelId);
  return (
    Boolean(componentData) &&
    relevantComponentTypeIds.has(componentData?.componentTypeId ?? '')
  );
};

type IncludeAllAncestors = (args: {
  relevantNodeIds: Set<string>;
  viewGraphHierarchy: TimelineViewGraphHierarchy;
  currentGraphItemId?: string;
  relevantChildrenMap: Map<string, (GraphGroup | GraphNode)[]>;
}) => {
  relevantNodeIds: Set<string>;
  relevantChildrenMap: Map<string, (GraphGroup | GraphNode)[]>;
};
const includeAllAncestorNodeIds: IncludeAllAncestors = ({
  relevantNodeIds,
  viewGraphHierarchy,
  currentGraphItemId,
  relevantChildrenMap,
}) => {
  if (
    !currentGraphItemId ||
    relevantNodeIds.has(currentGraphItemId) ||
    !viewGraphHierarchy.has(currentGraphItemId)
  ) {
    // if a node is already incuded into the set, its ancestors are already included
    return { relevantNodeIds, relevantChildrenMap };
  }
  const currentNodeHierarchyInfo = viewGraphHierarchy.get(currentGraphItemId)!;

  relevantNodeIds.add(currentNodeHierarchyInfo.graphItem.id);

  if (currentNodeHierarchyInfo.parentGraphNodeId) {
    relevantChildrenMap.set(
      currentNodeHierarchyInfo.parentGraphNodeId,
      getRelevantChildrenForCurrentParent(
        relevantChildrenMap,
        currentNodeHierarchyInfo.graphItem,
        currentNodeHierarchyInfo.parentGraphNodeId
      )
    );
  }

  return includeAllAncestorNodeIds({
    relevantNodeIds,
    viewGraphHierarchy,
    relevantChildrenMap,
    currentGraphItemId: currentNodeHierarchyInfo.parentGraphNodeId,
  });
};

const getRelevantChildrenForCurrentParent = (
  relevantChildrenMap: Map<string, (GraphGroup | GraphNode)[]>,
  currentGraphItem: GraphGroup | GraphNode,
  parentGraphNodeId: string
) =>
  relevantChildrenMap.get(parentGraphNodeId)?.concat([currentGraphItem]) ?? [
    currentGraphItem,
  ];

type GetRelevantNodeIdsForComponentTypesState = {
  relevantNodeIds: Set<string>;
  viewGraphHierarchy: TimelineViewGraphHierarchy;
  relevantComponentTypeIds: Set<string>;
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap;
  relevantChildrenMap: Map<string, (GraphGroup | GraphNode)[]>;
};
const getRelevantNodeIdsForComponentTypes = (
  state: GetRelevantNodeIdsForComponentTypesState,
  { graphItem, parentGraphNodeId }: TimelineViewNodeHierarchyInfo
) => {
  const {
    relevantNodeIds,
    relevantComponentTypeIds,
    viewRelevantComponentsScopeData,
    viewGraphHierarchy,
    relevantChildrenMap,
  } = state;

  if (
    // if we have it, don't add
    relevantNodeIds.has(graphItem.id) ||
    // if it is a component and it is NOT of relevant type, don't add
    (isNode(graphItem) &&
      !isComponentOfRelevantType(
        graphItem.modelId,
        relevantComponentTypeIds,
        viewRelevantComponentsScopeData
      ))
  ) {
    return state;
  }

  // Include the current node
  relevantNodeIds.add(graphItem.id);

  if (!parentGraphNodeId) {
    return state;
  }

  // create the relevant children map, so that we can build the relevant hierarchy from it
  relevantChildrenMap.set(
    parentGraphNodeId,
    getRelevantChildrenForCurrentParent(
      relevantChildrenMap,
      graphItem,
      parentGraphNodeId
    )
  );

  // Include all ancestors of the current node
  const {
    relevantNodeIds: nodeIdsWithAncestryOfCurrentNode,
    relevantChildrenMap: childrenMapWithAncestryOfCurrentNode,
  } = includeAllAncestorNodeIds({
    relevantNodeIds,
    viewGraphHierarchy,
    currentGraphItemId: parentGraphNodeId,
    relevantChildrenMap,
  });

  return {
    relevantNodeIds: nodeIdsWithAncestryOfCurrentNode,
    relevantChildrenMap: childrenMapWithAncestryOfCurrentNode,
    relevantComponentTypeIds,
    viewRelevantComponentsScopeData,
    viewGraphHierarchy,
  };
};

const ancestors = (
  viewGraphHierarchy: TimelineViewGraphHierarchy,
  parentGraphNodeId?: string
): TimelineViewNodeHierarchyInfo[] => {
  if (!parentGraphNodeId) {
    return [];
  }
  const ancestor = viewGraphHierarchy.get(parentGraphNodeId);
  return ancestor
    ? [ancestor, ...ancestors(viewGraphHierarchy, ancestor.parentGraphNodeId)]
    : [];
};

type RemoveIrrelevantViewGraphHierarchyArgs = {
  viewGraphHierarchy: TimelineViewGraphHierarchy;
  relevantComponentTypeIds: Set<string>;
  hasGrouping: boolean;
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap;
};
/**
 * Remove everything that is not of relevant component type (and has no relevant children)
 */
const removeIrrelevantViewGraphHierarchy = ({
  viewGraphHierarchy,
  relevantComponentTypeIds,
  hasGrouping,
  viewRelevantComponentsScopeData,
}: RemoveIrrelevantViewGraphHierarchyArgs): TimelineViewGraphHierarchy => {
  // If grouping is active, we want to show all children regardless of their type and whether or not
  // the components have date time fields.
  if (hasGrouping) {
    return new Map(
      [...viewGraphHierarchy.entries()].filter(
        ([_, { graphItem, children, parentGraphNodeId }]) =>
          children?.length ||
          isComponentOfRelevantType(
            graphItem.modelId,
            relevantComponentTypeIds,
            viewRelevantComponentsScopeData
          ) ||
          ancestors(viewGraphHierarchy, parentGraphNodeId).some(ancestor =>
            isComponentOfRelevantType(
              ancestor.graphItem.modelId,
              relevantComponentTypeIds,
              viewRelevantComponentsScopeData
            )
          )
      )
    );
  }

  // If no grouping is active, we only want to show components of relevant
  // type, and nodes that have descendants of relevant component type.
  const { relevantNodeIds, relevantChildrenMap } = Array.from(
    viewGraphHierarchy.values()
  ).reduce(getRelevantNodeIdsForComponentTypes, {
    relevantNodeIds: new Set<string>(),
    viewGraphHierarchy,
    relevantComponentTypeIds,
    viewRelevantComponentsScopeData,
    relevantChildrenMap: new Map(),
  });

  const relevantHierarchy: TimelineViewGraphHierarchy = new Map(
    Array.from(relevantNodeIds).map(relevantNodeId => {
      const nodeHierarchyInfo = viewGraphHierarchy.get(relevantNodeId)!;
      return [
        relevantNodeId,
        {
          graphItem: nodeHierarchyInfo.graphItem,
          children: relevantChildrenMap.get(relevantNodeId) ?? [],
          parentGraphNodeId: nodeHierarchyInfo.parentGraphNodeId,
          rawGraphItem: nodeHierarchyInfo.rawGraphItem,
        },
      ];
    })
  );

  return relevantHierarchy;
};

const geComponentTypeIdsForSelectedFields = (
  allWorkspaces: ViewRelevantWorkspaceScopeData[],
  allSelectedFieldIds: Set<string>
) =>
  new Set(
    allWorkspaces.flatMap(({ componentTypes, fieldsByComponentType }) =>
      componentTypes
        .filter(({ id: componentTypeId }) =>
          fieldsByComponentType
            .get(componentTypeId)
            ?.some(({ _id }) => allSelectedFieldIds.has(_id))
        )
        .map(({ id }) => id)
    )
  );

type RemoveIrrelevantNodesArgs = {
  viewRelevantWorkspacesScopeData: ViewRelevantWorkspaceScopeData[];
  allSelectedFieldIds: Set<string>;
  viewGraphHierarchy: TimelineViewGraphHierarchy;
  viewRelevantComponentsScopeData: RelevantCompontsScopeDataMap;
  contextComponentId: ArdoqId;
  hasGrouping: boolean;
  hasRootGroupsInContextOnlyFeature?: boolean;
};
export const removeIrrelevantNodesBasedOnSelectedFields = ({
  viewRelevantWorkspacesScopeData,
  allSelectedFieldIds,
  viewGraphHierarchy,
  hasGrouping,
  viewRelevantComponentsScopeData,
  contextComponentId,
  hasRootGroupsInContextOnlyFeature,
}: RemoveIrrelevantNodesArgs) => {
  // in viewpoint mode we don't need to filter by component type
  const typeIdsOfComponentsWithSelectedFields =
    geComponentTypeIdsForSelectedFields(
      viewRelevantWorkspacesScopeData,
      allSelectedFieldIds
    );

  /**
   * Remove everything that is not of relevant component type (and has no relevant children)
   */
  const filteredViewGraphHierarchy = removeIrrelevantViewGraphHierarchy({
    viewGraphHierarchy,
    relevantComponentTypeIds: typeIdsOfComponentsWithSelectedFields,
    hasGrouping,
    viewRelevantComponentsScopeData,
  });

  const hasChildren = (node: GraphItem) =>
    Boolean(filteredViewGraphHierarchy.get(node.id)?.children?.length);

  const viewGraphHierarchyRootItems =
    getViewGraphHierarchyRootItems(viewGraphHierarchy);

  const startSetNodes =
    hasRootGroupsInContextOnlyFeature && contextComponentId
      ? getRootItemsForContextComponent({
          allRootGraphItems: viewGraphHierarchyRootItems,
          contextComponentId,
          viewGraphHierarchy,
        })
      : viewGraphHierarchyRootItems;

  const filteredStartSetNodes = startSetNodes.filter(
    node =>
      hasChildren(node) ||
      isComponentOfRelevantType(
        node.modelId,
        typeIdsOfComponentsWithSelectedFields,
        viewRelevantComponentsScopeData
      )
  );

  return { filteredViewGraphHierarchy, filteredStartSetNodes };
};

type UpdateCollapsedArgs = {
  rawItemIdPath: string;
  isCollapsed: boolean;
  collapsedIds: Set<string>;
  collapsibleIds: Set<string>;
};
export const updateCollapsed = ({
  rawItemIdPath,
  isCollapsed,
  collapsedIds,
  collapsibleIds,
}: UpdateCollapsedArgs) => {
  const mutableCollapsedIds =
    !isCollapsed && collapsedIds.has(WILDCARD)
      ? new Set(collapsibleIds)
      : new Set(collapsedIds);

  if (isCollapsed) {
    mutableCollapsedIds.add(rawItemIdPath);
  } else {
    mutableCollapsedIds.delete(rawItemIdPath);
  }

  return mutableCollapsedIds;
};
type UpdateAllCollapsedArgs = {
  isCollapsed: boolean;
  collapsibleIds: Set<string>;
};
export const updateAllCollapsed = ({
  isCollapsed,
  collapsibleIds,
}: UpdateAllCollapsedArgs) =>
  isCollapsed ? new Set(collapsibleIds) : new Set<string>();

export const cleanUpCollapsedIds = (
  collapsedIds: Set<string>,
  collapsibleIds: Set<string>
) => {
  /**
   * The objective of this function is to prevent ever expanding collapsed ids.
   * If collapsible ids size is 0, the collapsed ids can't be expanded further,
   * so we don't care. Also this would keep the collapsed ids state when a slide
   * is reloaded, and the viewModel is recalculated completely, for example.
   *
   * If we want to reset the collapsed ids state on viewModel update => remove
   * the collapsibleGroups.size check
   */
  if (
    collapsedIds.has(WILDCARD) ||
    !collapsibleIds.size ||
    !collapsedIds.size
  ) {
    return collapsedIds;
  }

  const validCollapsedGroups = Array.from(collapsedIds).filter(rawItemIdPath =>
    collapsibleIds.has(rawItemIdPath)
  );

  return new Set(validCollapsedGroups);
};

type GetHoveredFieldsArgs = {
  hoverPosition: number;
  chartWidth: number;
  hoveredItemFields: TimelinePhasesWithRenderingParameters | null;
  itemPhasesRowIndex: number;
};

const HALF_POPOVER_MIN_WIDTH = 60;

const getHoveredFields = ({
  hoverPosition,
  chartWidth,
  hoveredItemFields,
  itemPhasesRowIndex,
}: GetHoveredFieldsArgs) => {
  const renderedPhases = hoveredItemFields
    ? getRenderedItemPhases(hoveredItemFields)
    : null;

  if (!renderedPhases) {
    return {
      hoveredPhases: [],
      hoveredMilestones: [],
    };
  }

  const hoveredItemRow = renderedPhases[itemPhasesRowIndex];

  return {
    hoveredPhases:
      hoveredItemRow?.phases.filter(
        ({ scaledStart, scaledEnd }) =>
          hoverPosition >= Math.max(scaledStart, 0) &&
          hoverPosition <=
            Math.min(scaledEnd, chartWidth - HALF_POPOVER_MIN_WIDTH)
      ) || [],
    hoveredMilestones:
      hoveredItemRow?.milestones.filter(
        ({ scaledStart }) =>
          hoverPosition >= Math.max(scaledStart, 0) &&
          hoverPosition <=
            Math.min(
              scaledStart + TIMELINE_MILESTONE_SIZE,
              chartWidth - HALF_POPOVER_MIN_WIDTH
            )
      ) || [],
  };
};

type GetHoveredFieldsPopoverPropsArgs = GetHoveredFieldsArgs & {
  adjustedGridPrecision: TimelinesGridPrecisionUnit;
  popoverX: number;
  popoverY: number;
  i18nLocale: I18nLocale;
};

export const getHoveredFieldsPopoverProps = ({
  hoverPosition,
  chartWidth,
  hoveredItemFields,
  adjustedGridPrecision,
  popoverX,
  popoverY,
  itemPhasesRowIndex,
  i18nLocale,
}: GetHoveredFieldsPopoverPropsArgs) => {
  const { hoveredPhases, hoveredMilestones } = getHoveredFields({
    hoverPosition,
    chartWidth,
    hoveredItemFields,
    itemPhasesRowIndex,
  });
  return hoveredPhases.length + hoveredMilestones.length > 0
    ? {
        popoverPhases: hoveredPhases,
        popoverMilestones: hoveredMilestones,
        precision: adjustedGridPrecision,
        popoverX,
        popoverY,
        popoverPlacement: WithPopoverPlacement.BOTTOM,
        i18nLocale,
      }
    : undefined;
};

export const getHighlightedItemIds = (
  hoveredItemId: string | null,
  focusedItemId: string | null,
  filteredViewGraphHierarchy: TimelineViewGraphHierarchy
): Set<string> => {
  const highlightedItemIds = new Set<string>();
  Array.from(filteredViewGraphHierarchy.values()).forEach(
    timelineViewNodeHierarchyInfo => {
      if (timelineViewNodeHierarchyInfo.graphItem.id === hoveredItemId) {
        withAllDescendants(
          timelineViewNodeHierarchyInfo.graphItem.id,
          filteredViewGraphHierarchy
        ).forEach(id => highlightedItemIds.add(id));
      }
      if (timelineViewNodeHierarchyInfo.graphItem.modelId === focusedItemId) {
        highlightedItemIds.add(timelineViewNodeHierarchyInfo.graphItem.id);
      }
    }
  );
  return highlightedItemIds;
};

const withWorkspaceName = (
  fieldLabel: string,
  fieldId: ArdoqId,
  workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry
) => {
  const fieldWorkspaceName = Array.from(
    workspaceAndFieldsRegistry.values()
  ).find(({ allDateFields }) =>
    allDateFields.some(({ _id }) => _id === fieldId)
  )?.name;

  return fieldWorkspaceName
    ? `${fieldLabel} [${fieldWorkspaceName}]`
    : fieldLabel;
};

/** This function adds workspace name to label if label is duplicated in the fields list */
export const withWorkspaceNameOnDuplicatedNames = <
  T extends DateRangeFieldAttributes | APIFieldAttributes,
>(
  fields: T[],
  workspaceAndFieldsRegistry: WorkspaceAndFieldsRegistry
) =>
  Object.values(groupBy(fields, ({ label }) => label)).flatMap(
    mileStoneFieldGroup =>
      mileStoneFieldGroup.length > 1
        ? mileStoneFieldGroup.map(mileStoneField => ({
            ...mileStoneField,
            label: withWorkspaceName(
              mileStoneField.label,
              mileStoneField._id,
              workspaceAndFieldsRegistry
            ),
          }))
        : mileStoneFieldGroup
  );

export const toPhase = (
  componentData: ViewRelevantComponentScopeData,
  timeSpanFields: DateRangeFieldAttributes,
  fieldIndex: number
): TimelinePhase | null => {
  const {
    dateTimeFields: { start: startField, end: endField },
  } = timeSpanFields;
  const startFieldName = startField.name;
  const endFieldName = endField.name;
  const startFieldValue = componentData.dateFieldValues[startFieldName];
  const endFieldValue = componentData.dateFieldValues[endFieldName];

  const adjustedEndFieldValue =
    endFieldValue && endField.type === APIFieldType.DATE_ONLY
      ? endOfDay(endFieldValue)
      : endFieldValue;

  if (!startFieldValue && !adjustedEndFieldValue) return null;

  return {
    name: timeSpanFields.name,
    label: timeSpanFields.label,
    type: timeSpanFields.type,
    start: startFieldValue,
    end: adjustedEndFieldValue,
    fieldIndex,
  };
};

export const toMilestone = (
  componentData: ViewRelevantComponentScopeData,
  milestoneField: APIFieldAttributes,
  fieldIndex: number
): Milestone | null => {
  const fieldValue = componentData.dateFieldValues[milestoneField.name];

  const adjustedFieldValue =
    fieldValue && milestoneField.type === APIFieldType.DATE_ONLY
      ? addHours(fieldValue, 12)
      : fieldValue;

  if (!adjustedFieldValue) return null;

  return {
    date: adjustedFieldValue,
    label: milestoneField.label,
    fieldIndex,
  };
};

export const findExistingFreeRowForPhase = (
  allRows: ItemPhasesRow[],
  phase: TimelinePhase
) =>
  allRows.find(
    ({ phasesBounds: { occupiedEnd } }) =>
      !occupiedEnd || occupiedEnd < (phase.start ?? MIN_DATE)
  );

/**
 * TODO: this should be enhanced with the width of the milestone converted to a date
 * range according to the current scale.
 *
 * For now we keep it as per legacy implementation (i.e. overlapping).
 */
export const findExistingFreeRowForMilestone = (
  allRows: ItemPhasesRow[],
  milestone: Milestone
) =>
  allRows.find(
    ({ milestonesBounds: { occupiedEnd } }) =>
      !occupiedEnd || occupiedEnd < milestone.date
  );

export const getOccupiedStartAndEndForPhase = (phase: TimelinePhase) => ({
  occupiedStart: phase.start ?? MIN_DATE,
  occupiedEnd: phase.end ?? MAX_DATE,
});

/**
 * TODO: this should be enhanced with the width of the milestone converted to a date
 * range according to the current scale.
 *
 * For now we keep it as per legacy implementation (i.e. overlapping).
 */
export const getOccupiedTimespanForMilestone = (milestone: Milestone) => ({
  occupiedStart: milestone.date,
  occupiedEnd: milestone.date,
});

export const findMinAndMaxForAllDates = (allItemDates: (Date | null)[]) => {
  if (!allItemDates.length) {
    return { occupiedStart: MIN_DATE, occupiedEnd: MAX_DATE };
  }

  const minStart = allItemDates.reduce((min, date) => {
    if (!date) {
      return MIN_DATE;
    }
    if (!min) {
      return date;
    }
    return date < min ? date : min;
  }, MAX_DATE);

  const maxEnd = allItemDates.reduce((max, date) => {
    if (!date) {
      return MAX_DATE;
    }
    if (!max) {
      return date;
    }
    return date > max ? date : max;
  }, MIN_DATE);

  return { occupiedStart: minStart, occupiedEnd: maxEnd };
};

/**
 * Requires phases to be sorted by start date.
 */
export const areSortedPhasesOverlapping = (
  sortedPhases: TimelinePhase[]
): boolean => {
  for (let i = 0; i < sortedPhases.length - 1; i++) {
    const currentPhase = sortedPhases[i];
    const nextPhase = sortedPhases[i + 1];

    const infiniteEndOverlapsWithNextPhase = !currentPhase.end && nextPhase;
    const infiniteStartOfNextOverlapsWithCurrentPhase =
      nextPhase && !nextPhase.start;

    if (
      infiniteEndOverlapsWithNextPhase ||
      infiniteStartOfNextOverlapsWithCurrentPhase
    ) {
      return true;
    }

    if (
      currentPhase.end &&
      nextPhase.start &&
      currentPhase.end >= nextPhase.start
    ) {
      return true;
    }
  }

  return false;
};

export const findLastChildIndex = (
  renderedItemLabels: TimelineLabelWithRenderingParameters[],
  currentGroupIndex: number
): number | null => {
  const currentItemTreeDepth =
    renderedItemLabels[currentGroupIndex].itemModel.treeDepth;
  let lastChildIndex: number | null = null;

  for (let i = currentGroupIndex + 1; i < renderedItemLabels.length; i++) {
    const nextItemTreeDepth = renderedItemLabels[i].itemModel.treeDepth;

    if (nextItemTreeDepth === currentItemTreeDepth + 1) {
      lastChildIndex = i;
    }

    if (nextItemTreeDepth <= currentItemTreeDepth) {
      break;
    }
  }

  return lastChildIndex;
};

export const getItemRowTotalHeight = (
  labelsData: TimelineLabelWithRenderingParameters,
  phasesData: TimelinePhasesWithRenderingParameters
) => {
  const totalLabelsHeight = getTotalLabelsHeight(labelsData);
  const totalPhasesHeight = getPhasesRowsHeightForItem(phasesData);

  return Math.max(totalLabelsHeight, totalPhasesHeight) + MARGIN_BETWEEN_ROWS;
};
